/**
 * Moteur des visites guidées — logique pure (05/10/2026, spec visite-guidee/2026-10-05-premiers-pas).
 * Une étape n'apparaît qu'à qui a le droit de l'écran : règle du menu (hasPermission sur l'org active).
 */
export const DELAI_CIBLE_MS = 5000;

// Sélecteurs invalides déjà signalés : trouverCible est sondée toutes les 100 ms, on ne prévient qu'une fois.
const selecteursInvalidesSignales = new Set();

// Premier repère présent ET affiché (taille non nulle) parmi `target` (sélecteur ou tableau de replis).
export function trouverCible(target, doc = document) {
  if (!target) return null;
  const selecteurs = Array.isArray(target) ? target : [target];
  for (const s of selecteurs) {
    let el = null;
    // Un sélecteur mal écrit compte comme absent : il ne doit pas figer la visite, mais il laisse une trace.
    try {
      el = doc.querySelector(s);
    } catch (err) {
      if (!selecteursInvalidesSignales.has(s)) {
        selecteursInvalidesSignales.add(s);
        console.warn('[visite] sélecteur invalide « %s »', s, err);
      }
      el = null;
    }
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    return el;
  }
  return null;
}

export function etapeAutorisee(step, droits) {
  if (droits.estSuperadmin) return true;
  if (step.permission && !droits.aPermission(step.permission)) return false;
  if (step.permissions && !step.permissions.some((p) => droits.aPermission(p))) return false;
  if (step.roles && !step.roles.includes(droits.roleEffectif)) return false;
  return true;
}

export function filtrerEtapes(steps, droits) {
  return (steps || []).filter((s) => etapeAutorisee(s, droits));
}

export function droitsDepuisAuth({ user, hasPermission, orgCode, getEffectiveRole }) {
  return {
    estSuperadmin: user?.role === 'superadmin',
    aPermission: (perm) => !!hasPermission(perm, orgCode),
    roleEffectif: getEffectiveRole(user, orgCode),
  };
}

// Route de l'étape : texte, ou fonction du contexte préparé (null = donnée absente).
export function resoudreRoute(step, ctx) {
  if (!step || !step.route) return null;
  const r = typeof step.route === 'function' ? step.route(ctx || {}) : step.route;
  return r || null;
}

// Attend que la cible apparaisse (page qui charge après un changement de route) ; null au délai.
export function attendreCible(target, { delaiMs = DELAI_CIBLE_MS, pasMs = 100, trouver = trouverCible } = {}) {
  if (!target) return Promise.resolve(null);
  return new Promise((resolve) => {
    const debut = Date.now();
    const tenter = () => {
      const el = trouver(target);
      if (el) return resolve(el);
      if (Date.now() - debut >= delaiMs) return resolve(null);
      setTimeout(tenter, pasMs);
    };
    tenter();
  });
}

// Reprise : index de l'étape mémorisée, 0 si elle n'existe plus (ou n'est plus autorisée).
export function indexDepart(steps, etapeId) {
  const i = (steps || []).findIndex((s) => s.id === etapeId);
  return i >= 0 ? i : 0;
}
